import React from 'react'

interface ListingResponse {
  id: string
  ownerId: string
  title: string
  description: string
  price: string
  category: string
  condition: string
  sportType: string
  mediaUrls: string[]
  status: string
  slug: string | null
  nfcUid: string | null
  createdAt: string
  updatedAt: string
  playerName: string | null
  teamOrClub: string | null
  leagueOrEvent: string | null
  dateOrSeason: string | null
  size: string | null
  tags: string[]
}

interface ListingCardProps {
  listing: ListingResponse
  isFavorite?: boolean
  onPress?: () => void
  onToggleFavorite?: (id: string) => void
}

// Helper function to format price from string to currency
function formatCurrency(priceString: string): string {
  const price = parseFloat(priceString)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price)
}

// Helper function to get category display name
function getCategoryDisplayName(category: string): string {
  switch (category) {
    case 'CLOTHING': return 'Clothing'
    case 'EQUIPMENT': return 'Equipment'
    case 'FOOTWEAR': return 'Footwear'
    case 'ACCESSORIES': return 'Accessories'
    default: return category
  }
}

// Helper function to get tag display styles
function getTagStyles(tag: string): { color: string; bg: string } {
  switch (tag) {
    case 'AUTOGRAPHED':
      return { color: 'text-blue-700', bg: 'bg-blue-100' }
    case 'LIMITED_EDITION':
      return { color: 'text-purple-700', bg: 'bg-purple-100' }
    case 'PLAYOFF_GAME':
      return { color: 'text-green-700', bg: 'bg-green-100' }
    case 'FINAL_MATCH':
      return { color: 'text-red-700', bg: 'bg-red-100' }
    case 'DEBUT_GAME':
      return { color: 'text-orange-700', bg: 'bg-orange-100' }
    case 'MVP_GAME':
      return { color: 'text-yellow-700', bg: 'bg-yellow-100' }
    default:
      return { color: 'text-gray-700', bg: 'bg-gray-100' }
  }
}

export default function ListingCard({ 
  listing, 
  isFavorite = false, 
  onPress, 
  onToggleFavorite 
}: ListingCardProps) {
  const handleClick = () => {
    if (onPress) {
      onPress()
    } else {
      console.log('View listing:', listing.id)
    }
  }

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click
    if (onToggleFavorite) {
      onToggleFavorite(listing.id)
    }
  }

  return (
    <div 
      onClick={handleClick}
      className="card hover:shadow-xl transition-all cursor-pointer relative group overflow-hidden"
      style={{ borderRadius: '20px' }}
    >
      {/* Heart Button */}
      <button
        onClick={handleToggleFavorite}
        className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/80 hover:bg-white transition-colors shadow-sm"
        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        <svg 
          className={`w-4 h-4 ${isFavorite ? 'text-red-500 fill-current' : 'text-gray-400'}`}
          viewBox="0 0 24 24"
        >
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
      </button>

      {/* Image - Desktop Card Style */}
      <div className="aspect-[4/3] overflow-hidden" style={{ background: 'var(--background-tertiary)' }}>
        {listing.mediaUrls && listing.mediaUrls.length > 0 ? (
          <img
            src={listing.mediaUrls[0]}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.parentElement!.innerHTML = `
                <div class="w-full h-full flex items-center justify-center bg-gray-100">
                  <svg class="w-12 h-12 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                </div>
              `
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--background-tertiary)' }}>
            <svg className="w-12 h-12" style={{ color: 'var(--text-muted)' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
          </div>
        )}
      </div>

      {/* Content - Desktop Card Style */}
      <div className="p-5 space-y-3">
        {/* Team/Category */}
        <p className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          {listing.teamOrClub || getCategoryDisplayName(listing.category)}
        </p>
        
        {/* Title */}
        <h3 className="text-lg font-semibold line-clamp-2 leading-tight" style={{ color: 'var(--text-primary)' }}>
          {listing.title}
        </h3>
        
        {/* Player Name */}
        {listing.playerName && (
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
            {listing.playerName}
          </p>
        )}

        {/* Tags - Desktop Style */}
        {listing.tags && listing.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {listing.tags.slice(0, 3).map((tag) => {
              const tagStyles = getTagStyles(tag)
              return (
                <span
                  key={tag}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${tagStyles.color} ${tagStyles.bg}`}
                >
                  {tag.replace(/_/g, ' ')}
                </span>
              )
            })}
            {listing.tags.length > 3 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100">
                +{listing.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Price - Desktop Style */}
        <div className="pt-2" style={{ borderTop: '1px solid var(--border-muted)' }}>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {formatCurrency(listing.price)}
          </p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Current bid
          </p>
        </div>
      </div>
    </div>
  )
}
